'use client';

import React, { useState } from 'react';
import { useRole, UserRole, ROLE_USERS } from '@/context/RoleContext';
import { ChevronDown, ShieldCheck, Briefcase, Users, User } from 'lucide-react';
import Icon from '@/components/ui/AppIcon';


const ROLE_CONFIG: Record<UserRole, { icon: React.ElementType; color: string; bg: string; description: string }> = {
  super_admin: {
    icon: ShieldCheck,
    color: 'text-primary',
    bg: 'bg-primary/10',
    description: 'Full access to all features',
  },
  payroll_manager: {
    icon: Briefcase,
    color: 'text-accent',
    bg: 'bg-accent/10',
    description: 'Payroll, timesheets & workers',
  },
  coordinator: {
    icon: Users,
    color: 'text-warning',
    bg: 'bg-warning/10',
    description: 'View timesheets & workers',
  },
  employee: {
    icon: User,
    color: 'text-success',
    bg: 'bg-success/10',
    description: 'Own payslips & timesheets',
  },
};

export default function RoleSwitcher({ collapsed }: { collapsed: boolean }) {
  const { currentUser, setRole } = useRole();
  const [open, setOpen] = useState(false);

  const config = ROLE_CONFIG[currentUser.role];
  const Icon = config.icon;

  return (
    <div className="relative">
      <button
        onClick={() => setOpen((v) => !v)}
        className={`w-full flex items-center gap-2.5 px-2.5 py-2 rounded-lg border border-border hover:bg-secondary transition-all duration-150 ${collapsed ? 'justify-center' : ''}`}
        title={collapsed ? `Role: ${currentUser.roleLabel}` : undefined}
      >
        <div className={`w-6 h-6 rounded-md flex items-center justify-center shrink-0 ${config.bg}`}>
          <Icon size={13} className={config.color} />
        </div>
        {!collapsed && (
          <>
            <div className="flex-1 min-w-0 text-left">
              <p className="text-[11px] font-600 text-foreground truncate leading-none mb-0.5">{currentUser.roleLabel}</p>
              <p className="text-[10px] text-muted-foreground truncate leading-none">{config.description}</p>
            </div>
            <ChevronDown size={12} className={`text-muted-foreground transition-transform duration-150 shrink-0 ${open ? 'rotate-180' : ''}`} />
          </>
        )}
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className={`absolute z-50 bg-card border border-border rounded-xl shadow-lg overflow-hidden ${collapsed ? 'left-full ml-2 bottom-0 w-52' : 'bottom-full mb-2 left-0 right-0'}`}>
            <div className="px-3 py-2 border-b border-border">
              <p className="text-[10px] font-600 uppercase tracking-widest text-muted-foreground">Switch Role (Demo)</p>
            </div>
            {(Object.keys(ROLE_USERS) as UserRole[]).map((role) => {
              const user = ROLE_USERS[role];
              const rc = ROLE_CONFIG[role];
              const RoleIcon = rc.icon;
              const isActive = currentUser.role === role;
              return (
                <button
                  key={role}
                  onClick={() => { setRole(role); setOpen(false); }}
                  className={`w-full flex items-center gap-2.5 px-3 py-2.5 hover:bg-secondary transition-all duration-150 ${isActive ? 'bg-secondary' : ''}`}
                >
                  <div className={`w-7 h-7 rounded-lg flex items-center justify-center shrink-0 ${rc.bg}`}>
                    <RoleIcon size={14} className={rc.color} />
                  </div>
                  <div className="flex-1 min-w-0 text-left">
                    <p className="text-[12px] font-600 text-foreground leading-none mb-0.5">{user.roleLabel}</p>
                    <p className="text-[10px] text-muted-foreground leading-none">{user.name}</p>
                  </div>
                  {isActive && (
                    <span className="w-1.5 h-1.5 rounded-full bg-primary shrink-0" />
                  )}
                </button>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
